import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { ICampaignCategory } from '../../../components/EsForm/CampaignCategorySelector';
import { emptyWizardCategory } from '../contestManage/contestCategoryWizardUtils';
import { reconcilePrizeStructureWithTiers } from './awardPresets';
import { CategoryBuilderStep } from './steps/CategoryBuilderStep';
import { CategoryAwardsStep } from './steps/CategoryAwardsStep';
import type { ContestWizardState } from './types';
import { brandContestDialogFooterSx, brandContestDialogPaperSx, editorialHeadlineSx, midnight } from './midnightStyles';
import { isQuickCreateCategoryAwardsConfigured, type ContestQuickCreateDraft } from './contestAutofillHelpers';
import { ContestTiersAwardsHelpButton } from './ContestTiersAwardsHelpButton';
import { plainTextFromHtml } from './richTextUtils';

type FlowStep = 'builder' | 'awards';

export interface ContestQuickCategoryAwardsDialogProps {
    open: boolean;
    categories: ICampaignCategory[];
    onClose: () => void;
    onComplete: (categories: ICampaignCategory[]) => void;
}

function sliceFromCategories(
    categories: ICampaignCategory[],
): Pick<ContestWizardState, 'categories_enabled' | 'categories' | 'editingCategoryIndex'> {
    return {
        categories_enabled: true,
        categories: categories.length ? categories : [emptyWizardCategory()],
        editingCategoryIndex: 0,
    };
}

export function ContestQuickCategoryAwardsDialog({
    open,
    categories,
    onClose,
    onComplete,
}: ContestQuickCategoryAwardsDialogProps) {
    const [flowStep, setFlowStep] = useState<FlowStep>('builder');
    const [slice, setSlice] = useState(() => sliceFromCategories(categories));

    useEffect(() => {
        if (open) {
            setFlowStep('builder');
            setSlice(sliceFromCategories(categories));
        }
    }, [open, categories]);

    const patch = (partial: Partial<ContestWizardState>) => setSlice(prev => ({ ...prev, ...partial }));

    const wizardState = useMemo(
        (): ContestWizardState =>
            ({
                categories_enabled: slice.categories_enabled,
                categories: slice.categories,
                editingCategoryIndex: slice.editingCategoryIndex,
            } as ContestWizardState),
        [slice],
    );

    const category = slice.categories[slice.editingCategoryIndex ?? 0] ?? emptyWizardCategory();
    const canContinueBuilder =
        Boolean(category.category_name?.trim()) && Boolean(plainTextFromHtml(category.category_description || ''));

    const draftForValidation = useMemo(
        (): ContestQuickCreateDraft => ({
            submission_state: 'public_submission',
            submission_state_message: '',
            title: '',
            description: '',
            requirements: [],
            categories_enabled: true,
            categories: slice.categories,
        }),
        [slice.categories],
    );

    const canCompleteAwards = isQuickCreateCategoryAwardsConfigured(draftForValidation);

    const handleContinueToAwards = () => {
        const idx = slice.editingCategoryIndex ?? 0;
        const cats = [...slice.categories];
        cats[idx] = reconcilePrizeStructureWithTiers(cats[idx] ?? emptyWizardCategory());
        setSlice(prev => ({ ...prev, categories: cats }));
        setFlowStep('awards');
    };

    const handleComplete = () => {
        onComplete(slice.categories);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            scroll="paper"
            PaperProps={{ sx: brandContestDialogPaperSx }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 3, pt: 2.5 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ ...editorialHeadlineSx, fontSize: 22, color: midnight.onSurface }}>
                            {flowStep === 'builder' ? 'Category builder' : 'Awards & prizes'}
                        </Typography>
                        <ContestTiersAwardsHelpButton />
                    </Box>
                    <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 13, mt: 0.75 }}>
                        {flowStep === 'builder'
                            ? 'Step 1 of 2 — define your submission category and tiers.'
                            : 'Step 2 of 2 — set cash prizes per placement and tier.'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label="Close" sx={{ color: midnight.onSurfaceVariant, mt: -0.5 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    px: 3,
                    py: 2,
                    overflow: 'auto',
                    maxHeight: { xs: 'calc(100vh - 220px)', sm: 'calc(92vh - 220px)' },
                }}>
                {flowStep === 'builder' ? (
                    <CategoryBuilderStep state={wizardState} onChange={patch} onOpenAiAssist={() => {}} />
                ) : (
                    <CategoryAwardsStep state={wizardState} onChange={patch} onOpenAiAssist={() => {}} />
                )}
            </Box>

            <Box
                sx={{
                    ...brandContestDialogFooterSx,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                {flowStep === 'awards' ? (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setFlowStep('builder')}
                        sx={{ color: midnight.onSurfaceVariant, fontWeight: 700 }}>
                        Back
                    </Button>
                ) : (
                    <Box />
                )}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            px: 3,
                            py: 1.25,
                            borderRadius: 999,
                            color: midnight.onSurfaceVariant,
                            fontWeight: 700,
                            fontSize: 12,
                            textTransform: 'uppercase',
                        }}>
                        Cancel
                    </Button>
                    {flowStep === 'builder' ? (
                        <Button
                            onClick={handleContinueToAwards}
                            disabled={!canContinueBuilder}
                            sx={{
                                px: 4,
                                py: 1.25,
                                borderRadius: 999,
                                fontWeight: 800,
                                fontSize: 12,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                color: '#392700',
                                background: `linear-gradient(90deg, ${midnight.primary}, #feb700)`,
                                '&:disabled': { opacity: 0.45 },
                            }}>
                            Continue to awards
                        </Button>
                    ) : (
                        <Button
                            onClick={handleComplete}
                            disabled={!canCompleteAwards}
                            sx={{
                                px: 4,
                                py: 1.25,
                                borderRadius: 999,
                                fontWeight: 800,
                                fontSize: 12,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                color: '#392700',
                                background: `linear-gradient(90deg, ${midnight.primary}, #feb700)`,
                                '&:disabled': { opacity: 0.45 },
                            }}>
                            Save category & awards
                        </Button>
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}
